import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  mergeClasses, Text, Button
} from '@fluentui/react-components';
import {
  ImageSearchRegular,
  CheckmarkRegular,
  SpinnerIosRegular,
  AddRegular,
} from '@fluentui/react-icons';
import {
  SectionTilesGrid,
  TilesGridCellImage,
  BlackSparkleSVGIcon,
  InfoSparkleSVGIcon,
  SquareDismissIcon,
  LoadingIndicator,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useGeneratedImagesStyles, useLoadingViewStyles } from './style';

const addSearchParams = (url, params = {}) =>
  new URL(`${url.origin}${url.pathname}?${new URLSearchParams([
    ...Array.from(url.searchParams.entries()),
    ...Object.entries(params),
  ])}`);

const MAXAIGCCount = 4;


const LoadingCell = () => {
  const classes = useLoadingViewStyles();

  return (
    <div className={mergeClasses('block', classes)} />
  );
};

const GeneratedImages = ({
  aigcImages,
  stockImages,
  i18n,
  isLoading,
  loadMoreStockImages,
  totalStockImagesCount,
  addAIGCImage,
  addStockImage,
  savedAIGCImages,
  savedStockImages,
  savingAIGCImages,
  savingStockImages,
  scrollContainer,
  cancel,
  logger,
  stopGeneratingBtnRef,
}) => {
  const generatedImagesStyle = useGeneratedImagesStyles();

  const onRenderImageSpinner = useCallback(() => (
    <LoadingCell />
  ), []);

  const Icon = useCallback(({ isSaved, isSaving }) => {
    if (isSaved) {
      return (
        <div className="icon">
          <CheckmarkRegular />
        </div>
      );
    } else if (isSaving) {
      return (
        <div className={mergeClasses('spinnerIcon', 'icon')}>
          <SpinnerIosRegular />
        </div>
      );
    }
    return (
      <div className={mergeClasses('addIcon', 'icon')}>
        <AddRegular />
      </div>
    );
  }, []);

  const AddButton = useCallback(({
    addImage,
    onKeyDown,
    isSaved,
    isSaving,
  }) => {
    let tooltipContent = '';
    if (isSaved) {
      tooltipContent = i18n.getString(_TL_('Asset saved'));
    } else if (isSaving) {
      tooltipContent = i18n.getString(_TL_('Saving'));
    } else {
      tooltipContent = i18n.getString(_TL_('Save to my library'));
    }

    return (
      <div className="action-overlay">
        <Tooltip
          withArrow
          appearance="inverted"
          relationship="label"
          content={tooltipContent}
        >
          <div className="add-button" role="button" onClick={addImage} onKeyDown={onKeyDown} tabIndex={0}>
            <div className={mergeClasses('add-button-view', isSaved || isSaving ? '' : 'notClicked', isSaved ? 'isClicked' : '')}>
              <Icon isSaved={isSaved} isSaving={isSaving} />
            </div>
          </div>
        </Tooltip>
      </div>
    );
  }, [Icon, i18n]);

  const onRenderAIGCCell = useCallback(({ item }) => {
    if (isLoading) {
      return <LoadingCell />;
    }

    let {
      thumbnailSrc: src,
    } = item;
    const { modelSource } = item;

    const isSaved = savedAIGCImages.includes(src);
    const isSaving = savingAIGCImages.includes(src);

    try {
      let srcURL = new URL(src);
      if (srcURL.host === 'th.bing.com') {
        const length = 330;
        srcURL = addSearchParams(srcURL, { w: length, h: length });
        src = srcURL.toString();
      }
    } catch (e) {
      // ignore
    }

    /* istanbul ignore next */
    const addImage = (event) => {
      if (isSaved || isSaving) { return; }
      if (logger && logger.logTrace) {
        logger.logTrace('Start to add aigc image');
      }
      addAIGCImage(src, modelSource);
      event.stopPropagation();
    };

    /* istanbul ignore next */
    const onKeyDown = (event) => {
      event.preventDefault();
      if (event.key === 'Enter') {
        addImage();
      }
    };

    return (
      <div className="aigc-cell">
        <TilesGridCellImage
          src={src}
          i18n={i18n}
          alt={i18n.getString(_TL_('AI generated image'))}
          showSpinner
          onCustomizedSpinner={onRenderImageSpinner}
        />
        <AddButton addImage={addImage} onKeyDown={onKeyDown} isSaved={isSaved} isSaving={isSaving} />
        <div className="info-overlay">
          <InfoSparkleSVGIcon />
        </div>
      </div>
    );
  }, [AddButton, addAIGCImage, i18n, isLoading, logger, onRenderImageSpinner, savedAIGCImages, savingAIGCImages]);

  const onRenderStockCell = useCallback(({ item }) => {
    const {
      Preview: { Url },
      Id,
    } = item;

    const isSaved = savedStockImages.includes(Id);
    const isSaving = savingStockImages.includes(Id);

    /* istanbul ignore next */
    const addImage = (event) => {
      if (isSaved || isSaving) { return; }
      if (logger && logger.logTrace) {
        logger.logTrace('Start to add stock image');
      }
      addStockImage(Id);
      event.stopPropagation();
    };

    /* istanbul ignore next */
    const onKeyDown = (event) => {
      event.preventDefault();
      if (event.key === 'Enter') {
        addImage();
      }
    };

    return (
      <div className="stock-cell">
        <TilesGridCellImage
          src={Url}
          i18n={i18n}
          alt={i18n.getString(_TL_('Stock image'))}
        />
        <AddButton addImage={addImage} onKeyDown={onKeyDown} isSaved={isSaved} isSaving={isSaving} />
      </div>
    );
  }, [savedStockImages, savingStockImages, i18n, AddButton, logger, addStockImage]);

  const aigcImagesWithAction = useMemo(() => aigcImages.map((image) => {
    const {
      thumbnailSrc: src,
      modelSource,
    } = image;

    const isSaved = savedAIGCImages.includes(src);
    const isSaving = savingAIGCImages.includes(src);

    return ({
      ...image,
      onAction: () => {
        /* istanbul ignore if */
        if (isSaved || isSaving) { return; }
        /* istanbul ignore next */
        addAIGCImage(src, modelSource);
      },
    });
  }), [aigcImages, savedAIGCImages, savingAIGCImages, addAIGCImage]);

  const stockImagesWithAction = useMemo(() => stockImages.map((image) => {
    const {
      Id,
    } = image;

    const isSaved = savedStockImages.includes(Id);
    const isSaving = savingStockImages.includes(Id);

    return ({
      ...image,
      onAction: () => {
        /* istanbul ignore if */
        if (isSaved || isSaving) { return; }
        /* istanbul ignore next */
        addStockImage(Id);
      },
    });
  }), [stockImages, savedStockImages, savingStockImages, addStockImage]);

  const aigcImagesGridSection = useMemo(() => ({
    sectionName: 'AIGCImages',
    sectionClassName: 'aigc-image-section',
    itemKey: 'id',
    items: isLoading ? Array.from({ length: MAXAIGCCount }, (v, k) => ({ k })) : aigcImagesWithAction,
    totalItemCount: isLoading ? MAXAIGCCount : aigcImagesWithAction.length,
    config: {
      width: 285,
      height: 285,
      gridMode: true,
      maxColumns: 2,
      columnPadding: 16,
      rowPadding: 16,
      scrollContainer: scrollContainer !== undefined ? scrollContainer : '[role="main"]',
    },
  }), [aigcImagesWithAction, isLoading, scrollContainer]);

  const stockImagesGridSection = useMemo(() => ({
    sectionName: 'StockImages',
    sectionClassName: 'stock-image-section',
    itemKey: 'id',
    items: stockImagesWithAction,
    totalItemCount: totalStockImagesCount,
    config: {
      width: 285,
      maxColumns: 2,
      columnPadding: 16,
      rowPadding: 16,
      scrollContainer: scrollContainer !== undefined ? scrollContainer : '[role="main"]',
    },
  }), [scrollContainer, stockImagesWithAction, totalStockImagesCount]);

  return (
    <div className={mergeClasses('generated-images', generatedImagesStyle)}>
      <div className="aigc-images">
        <div className="title">
          <BlackSparkleSVGIcon />
          <span role="heading" aria-level="2">
            {i18n.getString(_TL_('AI generated'))}
          </span>
        </div>
        {
          isLoading && (
            <div className="loading-section">
              <LoadingIndicator />
              <Button
                icon={<SquareDismissIcon i18n={i18n} />}
                appearance="subtle"
                onClick={() => {
                  cancel();
                }}
                ref={stopGeneratingBtnRef}
              >
                {i18n.getString(_TL_('Stop generating'))}
              </Button>
            </div>
          )
        }
        <SectionTilesGrid
          sectionData={[aigcImagesGridSection]}
          onRenderCell={onRenderAIGCCell}
        />
        <div className="actionBottomText" >
          {isLoading ? null : <Text align="start">{i18n.getString(_TL_('Created with Microsoft Advertising'))}</Text>}
        </div>
      </div>
      {!isLoading && stockImages && stockImages.length > 0 &&
        (
          <div className="stock-images">
            <div className="title">
              <ImageSearchRegular />
              <span role="heading" aria-level="2">
                {i18n.getString(_TL_('Stock images'))}
              </span>
            </div>
            <SectionTilesGrid
              sectionData={[stockImagesGridSection]}
              onRenderCell={onRenderStockCell}
              loadNextPage={loadMoreStockImages}
            />
          </div>
        )
      }
    </div>
  );
};

GeneratedImages.propTypes = {
  stockImages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  aigcImages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export {
  GeneratedImages,
};
